/**
 * 注目案件管理用の API hooks
 */
import { useCallback, useEffect, useState } from "react";
import { RestApiError, restClient } from "../lib/rest-client";

/**
 * 注目案件アイテムの型
 */
export type FocusMatterItem = {
  id: string;
  title: string;
  clientCompany: string | null;
  status: string;
  rewardMin: number | null;
  rewardMax: number | null;
  remoteType: string | null;
  nearestStation: string | null;
  deadline: string | null;
  requiredSkills: string[];
  niceToHaveSkills: string[];
  notes: string | null;
  sourceEmailId: string | null;
  assignedTo: string | null;
  assigneeName: string | null;
  candidateCount: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * 注目案件一覧レスポンスの型
 */
type FocusMatterListResponse = {
  items: FocusMatterItem[];
  total: number;
  page: number;
  limit: number;
};

/**
 * 注目案件フィルターの型
 */
export type FocusMatterFilters = {
  status?: string;
  search?: string;
  assignedTo?: string;
  page?: number;
  limit?: number;
};

/**
 * フィルターをクエリパラメータに変換
 * @param {object} filters フィルター
 * @return {Record<string, string>} クエリパラメータ
 */
function filtersToParams(filters: FocusMatterFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.status) params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.assignedTo) params.assignedTo = filters.assignedTo;
  if (filters.page !== undefined) params.page = String(filters.page);
  if (filters.limit !== undefined) params.limit = String(filters.limit);
  return params;
}

/**
 * 注目案件一覧を取得する hook
 * @param {object} filters フィルター条件
 * @param {string} [filters.status] ステータス
 * @param {string} [filters.search] 検索キーワード
 * @param {string} [filters.assignedTo] 担当者
 * @param {number} [filters.page] ページ番号
 * @param {number} [filters.limit] 取得件数
 * @return {object} matters, total, loading, error, refetch
 */
export function useFocusMatters(filters: FocusMatterFilters = {}): {
  matters: FocusMatterItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [matters, setMatters] = useState<FocusMatterItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const filterKey = JSON.stringify(filters);

  const fetchMatters = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = filtersToParams(
        JSON.parse(filterKey) as FocusMatterFilters,
      );
      const response = await restClient.get<FocusMatterListResponse>(
        "/focus-matters",
        params,
      );
      setMatters(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  return { matters, total, loading, error, refetch: fetchMatters };
}

/**
 * 注目案件を作成する hook
 * @return {object} createMatter, loading, error
 */
export function useCreateFocusMatter(): {
  createMatter: (data: Partial<FocusMatterItem>) => Promise<FocusMatterItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const createMatter = useCallback(
    async (data: Partial<FocusMatterItem>): Promise<FocusMatterItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<FocusMatterItem>(
          "/focus-matters",
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createMatter, loading, error };
}

/**
 * 注目案件を更新する hook
 * @return {object} updateMatter, loading, error
 */
export function useUpdateFocusMatter(): {
  updateMatter: (
    id: string,
    data: Partial<FocusMatterItem>,
  ) => Promise<FocusMatterItem>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateMatter = useCallback(
    async (
      id: string,
      data: Partial<FocusMatterItem>,
    ): Promise<FocusMatterItem> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.put<FocusMatterItem>(
          `/focus-matters/${id}`,
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateMatter, loading, error };
}

/**
 * 注目案件を削除する hook
 * @return {object} deleteMatter, loading, error
 */
export function useDeleteFocusMatter(): {
  deleteMatter: (id: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const deleteMatter = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await restClient.delete(`/focus-matters/${id}`);
    } catch (e) {
      const apiError =
        e instanceof RestApiError
          ? e
          : new RestApiError(
              e instanceof Error ? e.message : "Unknown error",
              500,
            );
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteMatter, loading, error };
}
